import { EventEmitter, Injectable, Output } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CNMedication, CNMedicationType } from '../models/cnmedication';
import { CNPatient } from '../models/cnpatient';
import { CNTreatment, CNTreatmentType } from '../models/cntreatment';
import { SNItemFactory } from '../models/snitemfactory';
import { BecareService } from './becare.service';
import { ContactService } from './contact.service';
import { PatientService } from './patient.service';

export enum TreatmentServiceFilter {
  all = 'all',
  all_active = 'all_active',
  all_notactive = 'all_notactive',
  active_medication = 'active_medication',
  notactive_medication = 'notactive_medication',
  active_treatment = 'active_treatment',
  notactive_treatment = 'notactive_treatment'
}

export class TreatmentServiceSubject {
  private service: TreatmentService;
  private becareService: BecareService;
  private myPatient: CNPatient;
  private myName: string;
  private myFilter: TreatmentServiceFilter;
  private paged: boolean | number;
  private buffer: CNMedication[];
  public treatments: BehaviorSubject<(CNMedication | CNTreatment)[]>;
  private myStart: number;
  private myLimit: number;
  public length: number;

  private medicationUrl = '';

  constructor(service: TreatmentService, becare: BecareService, patient: CNPatient,
              name: string, filter: TreatmentServiceFilter, paged: boolean | number) {
    this.service = service;
    this.becareService = becare;
    this.myName = name;
    this.myFilter = filter;
    this.paged = paged;
    if (paged) {
      this.myStart = 0;
      this.myLimit = (typeof paged === 'number') ? paged : 0; // No limit if not paged
      this.length = 0;
    }
    this.treatments = new BehaviorSubject<(CNMedication | CNTreatment)[]>([]);
    // Set patient, build rest url and query first buffer
    this.patient = patient;
  }

  public load() {
    if (!this.medicationUrl) {
      return;
    }
    this.becareService.call(this.medicationUrl).subscribe(result => {
      if (result.success === true && result.medications !== undefined) {
        const factory = new SNItemFactory();
        this.buffer = factory.itemsFromJson(result.medications);
        this.length = result.total;
        this.service.addTreatments(this.buffer);
        this.treatments.next(this.buffer);
      }
      if (result.success === true && result.treatments !== undefined) {
        const factory = new SNItemFactory();
        this.buffer = factory.itemsFromJson(result.treatments);
        this.length = result.total;
        this.service.addTreatments(this.buffer);
        this.treatments.next(this.buffer);
      }
    });
  }

  private buildUrl() {
    if (this.paged) {
      this.medicationUrl = '/fr/patient/' + this.myPatient.id + '/treatment?start=' + this.myStart + '&limit=' + this.myLimit;
    } else {
      switch (this.myFilter) {
        case TreatmentServiceFilter.active_medication:
          this.medicationUrl = '/fr/patient/' + this.myPatient.id + '/medication/active';
          break;
        case TreatmentServiceFilter.notactive_medication:
          this.medicationUrl = '/fr/patient/' + this.myPatient.id + '/medication/notactive';
          break;
        case TreatmentServiceFilter.active_treatment:
          this.medicationUrl = '/fr/patient/' + this.myPatient.id + '/treatment/active';
          break;
        case TreatmentServiceFilter.notactive_treatment:
          this.medicationUrl = '/fr/patient/' + this.myPatient.id + '/treatment/notactive';
          break;
        default:
          this.medicationUrl = '';
      }
    }
  }

  set patient(patient: CNPatient) {
    this.myPatient = patient;
    if (!this.myPatient) {
      return;
    }
    this.buildUrl();
    this.load();
  }

  set start(start: number) {
    this.myStart = start;
    this.buildUrl();
    this.load();
  }

  get start(): number {
    return this.myStart;
  }

  set limit(limit: number) {
    this.myLimit = limit;
    this.buildUrl();
    this.load();
  }
}

@Injectable({
  providedIn: 'root'
})
export class TreatmentService {

  private static instance: TreatmentService = null;
  private currentpatient: CNPatient;
  private subjects: Map<string, TreatmentServiceSubject> = new Map();
  private treatmentTypeIconMapping: Map<string, string[]> = new Map();

  public treatments: Map<number, (CNMedication | CNTreatment)> = new Map();

  @Output() clear = new EventEmitter<boolean>();

  constructor(private becare: BecareService, private patient: PatientService, private contacts: ContactService) {
    TreatmentService.instance = this;
    this.patient.patientSelectedEvent.subscribe(patientselected => {
      this.currentpatient = patientselected;
      this.treatments = new Map();
      this.subjects.forEach((subject: TreatmentServiceSubject, name: string) => {
        subject.patient = this.currentpatient;
      });
      this.clear.emit(true);
    });
    this.contacts.saved.subscribe(savedConsultation => {
      this.reloadAllSubjects();
    });
    this.treatmentTypeIconMapping.set(CNTreatmentType.Kine, ['fas', 'child']);
    this.treatmentTypeIconMapping.set(CNTreatmentType.Logo, ['fas', 'comment']);
  }

  public static getInstance(): TreatmentService {
    return TreatmentService.instance;
  }

  public getSubject(name: string, filter?: TreatmentServiceFilter, paged?: boolean | number): TreatmentServiceSubject {
    if (this.subjects.has(name)) {
      return this.subjects.get(name);
    }
    if (!filter && !paged) {
      return undefined;
    }
    // Build new service subject
    const newSubject = new TreatmentServiceSubject(this, this.becare, this.currentpatient, name, filter, paged);
    this.subjects.set(name, newSubject);
    return newSubject;
  }

  public reloadAllSubjects() {
    this.subjects.forEach((value, key) => {
      value.load();
    });
  }

  public addTreatments(treatments: (CNMedication | CNTreatment)[]): void {
    treatments.forEach(treatment => {
      if (!this.treatments.has(treatment.id)) {
        this.treatments.set(treatment.id, treatment);
      }
    });
  }

  public typeIcon(type: (CNMedicationType | CNTreatmentType)): string[] {
    if (this.treatmentTypeIconMapping.has(type)) {
      return this.treatmentTypeIconMapping.get(type);
    }
    return undefined;
  }

  public save(treatment: CNTreatment): void {
    this.becare.call('/fr/patient/' + this.patient.patient.id + '/medication', [], treatment, 'put', 'treatment', true).subscribe(item => {
      this.reloadAllSubjects();
    });
  }
}
